import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MdArrowBack, MdSave, MdDelete, MdShoppingCart, MdWorkspaces,
  MdAdd, MdPendingActions, MdPlayArrow, MdCheckCircle, MdCancel,
  MdWarning, MdDone, MdReceipt, MdChecklist, MdAttachMoney,
  MdStickyNote2, MdCalendarMonth, MdCircle, MdFlag, MdNote,
  MdKeyboardArrowUp, MdAttachFile, MdOpenInNew, MdInsertDriveFile,
  MdImage, MdPictureAsPdf, MdTableChart
} from 'react-icons/md';
import { pedidosAPI, inventarioAPI, clientesAPI, recibosAPI, proyectosAPI, adjuntosAPI } from '../services/api';
import { useModal } from '../context/ModalContext';

/* ─── Layout ─── */
const Container = styled.div`width: 100%; max-width: 100%;`;

const PageHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.xl}; flex-wrap: wrap; gap: ${p => p.theme.spacing.md};
`;
const HeaderLeft = styled.div`display: flex; align-items: center; gap: ${p => p.theme.spacing.lg};`;
const BackButton = styled.button`
  width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary}; cursor: pointer;
  svg { font-size: 1.5rem; }
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;
const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl}; color: ${p => p.theme.colors.textPrimary}; margin: 0;
  display: flex; align-items: center; gap: 10px;
  svg { color: ${p => p.theme.colors.primary}; }
`;
const HeaderActions = styled.div`display: flex; gap: 8px; align-items: center;`;
const DeleteBtn = styled.button`
  padding: 8px 16px; border: 1px solid ${p => p.theme.colors.danger}50;
  border-radius: ${p => p.theme.borderRadius.md}; background: ${p => p.theme.colors.danger}10;
  color: ${p => p.theme.colors.danger}; font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600;
  font-family: ${p => p.theme.fonts.primary}; display: flex; align-items: center; gap: 6px;
  cursor: pointer; svg { font-size: 1.1rem; }
  &:hover { background: ${p => p.theme.colors.danger}20; }
`;
const GenReciboBtn = styled.button`
  padding: 8px 16px; border: 1px solid ${p => p.theme.colors.primary}50;
  border-radius: ${p => p.theme.borderRadius.md}; background: ${p => p.theme.colors.primary}10;
  color: ${p => p.theme.colors.primary}; font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600;
  font-family: ${p => p.theme.fonts.primary}; display: flex; align-items: center; gap: 6px;
  cursor: pointer; svg { font-size: 1.1rem; }
  &:hover { background: ${p => p.theme.colors.primary}20; }
`;

/* ─── Tabs ─── */
const TabBar = styled.div`
  display: flex; gap: 2px; margin-bottom: ${p => p.theme.spacing.xl};
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 4px; box-shadow: ${p => p.theme.shadows.card};
  overflow-x: auto;
`;
const Tab = styled.button`
  flex: 1; min-width: 100px; padding: 10px 14px;
  border: none; border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.active
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : 'transparent'};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: ${p => p.active ? 700 : 400};
  font-family: ${p => p.theme.fonts.primary}; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: all 0.15s; white-space: nowrap;
  svg { font-size: 1.1rem; }
  &:hover { background: ${p => p.active ? undefined : p.theme.colors.background}; color: ${p => p.active ? 'white' : p.theme.colors.textPrimary}; }
`;

/* ─── Status Strip ─── */
const StatusStrip = styled.div`
  display: flex; gap: ${p => p.theme.spacing.sm}; margin-bottom: ${p => p.theme.spacing.xl}; flex-wrap: wrap;
`;
const StatusStep = styled.button`
  flex: 1; min-width: 120px; padding: 10px;
  border: 2px solid ${p => p.active ? p.color : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.active ? p.color + '15' : p.theme.colors.background};
  color: ${p => p.active ? p.color : p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: ${p => p.active ? 700 : 400};
  font-family: ${p => p.theme.fonts.primary}; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s;
  svg { font-size: 1.1rem; }
  &:hover { border-color: ${p => p.color}; color: ${p => p.color}; background: ${p => p.color}10; }
`;

/* ─── Cards ─── */
const FormCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl}; box-shadow: ${p => p.theme.shadows.card};
  margin-bottom: ${p => p.theme.spacing.xl};
`;
const CardTitle = styled.h3`
  font-size: ${p => p.theme.fontSizes.lg}; color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0; font-weight: 700;
  padding-bottom: ${p => p.theme.spacing.md}; border-bottom: 1px solid ${p => p.theme.colors.border};
  display: flex; align-items: center; gap: 8px;
  svg { color: ${p => p.theme.colors.primary}; }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${p => p.theme.spacing.lg};
`;
const FieldGroup = styled.div`display: flex; flex-direction: column; gap: ${p => p.theme.spacing.sm};`;
const Label = styled.label`font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; color: ${p => p.theme.colors.textPrimary};`;
const Input = styled.input`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;
const Select = styled.select`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;
const TextArea = styled.textarea`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary}; font-family: ${p => p.theme.fonts.primary};
  min-height: 80px; resize: vertical;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;
const ButtonRow = styled.div`
  display: flex; gap: ${p => p.theme.spacing.md}; justify-content: flex-end;
`;
const Btn = styled.button`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  background: ${p => p.primary
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.danger ? p.theme.colors.danger + '15'
    : p.theme.colors.backgroundLight};
  color: ${p => p.primary ? 'white' : p.danger ? p.theme.colors.danger : p.theme.colors.textPrimary};
  border: 1px solid ${p => p.primary ? p.theme.colors.primary : p.danger ? p.theme.colors.danger + '50' : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600;
  font-family: ${p => p.theme.fonts.primary}; display: flex; align-items: center; gap: 6px;
  cursor: pointer; transition: ${p => p.theme.transitions.fast};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

/* ─── Items (card grid) ─── */
const ItemRow = styled.div`
  display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 40px;
  gap: ${p => p.theme.spacing.md}; align-items: end;
  margin-bottom: ${p => p.theme.spacing.md}; padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md}; border: 1px solid ${p => p.theme.colors.border};
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;
const ItemFieldGroup = styled.div`display: flex; flex-direction: column; gap: ${p => p.theme.spacing.xs};`;
const ItemLabel = styled.label`font-size: ${p => p.theme.fontSizes.xs}; font-weight: 600; color: ${p => p.theme.colors.textSecondary};`;
const ItemInput = styled.input`
  width: 100%; padding: 8px ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &[readonly] { background: ${p => p.theme.colors.background}; cursor: not-allowed; }
`;
const DelBtn = styled.button`
  width: 32px; height: 32px; border: none; border-radius: 6px;
  background: ${p => p.theme.colors.danger}10; color: ${p => p.theme.colors.danger};
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  svg { font-size: 1rem; }
  &:hover { background: ${p => p.theme.colors.danger}20; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const AddItemBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 14px; margin-top: 12px;
  border: 1.5px dashed ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  background: none; color: ${p => p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm}; font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;
const TotalRow = styled.div`
  display: flex; justify-content: flex-end; margin-top: 16px;
  font-size: ${p => p.theme.fontSizes.xl}; font-weight: 800; color: ${p => p.theme.colors.primary};
`;
const StockTag = styled.div`
  font-size: 10px; font-weight: 700; margin-top: 3px;
  color: ${p => p.warn ? p.theme.colors.danger : p.theme.colors.success};
  display: flex; align-items: center; gap: 3px;
`;
const StockBanner = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 8px 14px;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; margin-bottom: 12px;
  background: ${p => p.success ? p.theme.colors.success + '18' : p.theme.colors.warning + '18'};
  color: ${p => p.success ? p.theme.colors.success : p.theme.colors.warning};
  svg { font-size: 1.1rem; }
`;

/* ─── Generar Recibo ─── */
const ReciboForm = styled.div`
  background: ${p => p.theme.colors.background}; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl}; display: flex; flex-direction: column; gap: ${p => p.theme.spacing.md};
`;
const ReciboFormRow = styled.div`display: flex; align-items: flex-end; gap: ${p => p.theme.spacing.md}; flex-wrap: wrap;`;
const ReciboLabel = styled.label`
  font-size: ${p => p.theme.fontSizes.xs}; font-weight: 700; color: ${p => p.theme.colors.textSecondary};
  text-transform: uppercase; letter-spacing: 0.05em; display: block; margin-bottom: 4px;
`;
const ReciboInput = styled.input`
  padding: 8px 12px; border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;
const ReciboBtn = styled.button`
  padding: 8px 18px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white; border: none; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; font-family: ${p => p.theme.fonts.primary};
  display: flex; align-items: center; gap: 6px; cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/* ─── Sticky resumen ─── */
const StickyResumen = styled.div`
  position: sticky; top: 0; z-index: 20;
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg} ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  margin-bottom: ${p => p.theme.spacing.xl};
  border: 1px solid ${p => p.theme.colors.border};
`;
const ResumenRow = styled.div`
  display: flex; align-items: center; gap: ${p => p.theme.spacing.xl}; flex-wrap: wrap;
`;
const ResumenStat = styled.div`
  display: flex; flex-direction: column; align-items: center; min-width: 60px;
`;
const ResumenNum = styled.div`
  font-size: ${p => p.theme.fontSizes.xl}; font-weight: 800;
  color: ${p => p.color || p.theme.colors.primary};
`;
const ResumenLabel = styled.div`
  font-size: 10px; color: ${p => p.theme.colors.textSecondary}; font-weight: 600;
  text-transform: uppercase; margin-top: 1px; white-space: nowrap;
`;
const ResumenDivider = styled.div`
  width: 1px; height: 36px; background: ${p => p.theme.colors.border}; flex-shrink: 0;
`;
const ResumenProgress = styled.div`flex: 1; min-width: 160px;`;
const ResumenProgressLabel = styled.div`
  display: flex; justify-content: space-between;
  font-size: ${p => p.theme.fontSizes.xs}; font-weight: 600;
  color: ${p => p.theme.colors.textSecondary}; margin-bottom: 4px;
`;
const ProgressBar = styled.div`
  width: 100%; height: 10px; background: ${p => p.theme.colors.borderLight};
  border-radius: 99px; overflow: hidden; margin-top: 6px;
`;
const ProgressFill = styled.div`
  height: 100%; width: ${p => p.pct}%;
  background: linear-gradient(90deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  transition: width 0.4s ease;
`;

/* ─── Tareas ─── */
const TareaList = styled.div`display: flex; flex-direction: column; gap: 10px;`;
const TareaItem = styled.div`
  display: flex; align-items: flex-start; gap: 12px;
  padding: 12px 16px; border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background}; border: 1px solid ${p => p.theme.colors.border};
  border-left: 3px solid ${p => p.color || p.theme.colors.border};
  opacity: ${p => p.done ? 0.6 : 1};
`;
const TareaCheck = styled.button`
  width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; margin-top: 2px;
  border: 2px solid ${p => p.done ? p.theme.colors.success : p.theme.colors.border};
  background: ${p => p.done ? p.theme.colors.success : 'transparent'};
  color: white; display: flex; align-items: center; justify-content: center; cursor: pointer;
  svg { font-size: 0.75rem; }
  &:hover { border-color: ${p => p.theme.colors.success}; }
`;
const TareaInfo = styled.div`flex: 1; min-width: 0;`;
const TareaTitulo = styled.div`
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; color: ${p => p.theme.colors.textPrimary};
  text-decoration: ${p => p.done ? 'line-through' : 'none'};
`;
const TareaDesc = styled.div`font-size: ${p => p.theme.fontSizes.xs}; color: ${p => p.theme.colors.textSecondary}; margin-top: 2px;`;
const TareaMeta = styled.div`display: flex; gap: 8px; margin-top: 6px; flex-wrap: wrap;`;
const Badge = styled.span`
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 99px;
  background: ${p => p.bg || '#eee'}20; color: ${p => p.bg || '#888'};
  border: 1px solid ${p => p.bg || '#eee'}40;
`;
const AddForm = styled.div`
  background: ${p => p.theme.colors.background}; border: 1px dashed ${p => p.theme.colors.primary}50;
  border-radius: ${p => p.theme.borderRadius.md}; padding: ${p => p.theme.spacing.lg};
  margin-top: ${p => p.theme.spacing.lg};
  display: flex; flex-direction: column; gap: ${p => p.theme.spacing.md};
`;
const AddFormRow = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: ${p => p.theme.spacing.md};`;
const SmInput = styled.input`
  width: 100%; padding: 8px 10px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.backgroundLight}; color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;
const SmSelect = styled.select`
  width: 100%; padding: 8px 10px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.backgroundLight}; color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;
const SmTextArea = styled.textarea`
  width: 100%; padding: 8px 10px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.backgroundLight}; color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary}; resize: vertical; min-height: 60px;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;
const ToggleAddBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 14px; margin-top: 12px;
  border: 1.5px dashed ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  background: none; color: ${p => p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm}; font-family: ${p => p.theme.fonts.primary}; cursor: pointer;
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;
const SaveBtn = styled.button`
  align-self: flex-end; padding: 8px 20px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white; border: none; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; font-family: ${p => p.theme.fonts.primary};
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/* ─── Gastos ─── */
const GastoTable = styled.table`width: 100%; border-collapse: collapse;`;
const GTh = styled.th`
  padding: 8px 12px; text-align: left; font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700; color: ${p => p.theme.colors.textLight}; text-transform: uppercase;
  border-bottom: 2px solid ${p => p.theme.colors.border};
`;
const GTd = styled.td`
  padding: 10px 12px; font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary}; border-bottom: 1px solid ${p => p.theme.colors.borderLight};
`;
const GastoTotal = styled.div`
  display: flex; justify-content: flex-end; margin-top: 16px; gap: 16px;
  font-size: ${p => p.theme.fontSizes.lg}; font-weight: 800; color: ${p => p.theme.colors.primary};
`;
const CatBadge = styled.span`
  font-size: 11px; padding: 2px 8px; border-radius: 99px; font-weight: 600;
  background: ${p => p.theme.colors.primary}15; color: ${p => p.theme.colors.primary};
`;

/* ─── Notas ─── */
const NotaList = styled.div`display: flex; flex-direction: column; gap: 12px;`;
const NotaItem = styled.div`
  padding: 14px 16px; background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md}; border: 1px solid ${p => p.theme.colors.border};
  border-left: 3px solid ${p => p.theme.colors.primary};
  position: relative;
`;
const NotaFecha = styled.div`font-size: 10px; color: ${p => p.theme.colors.textLight}; margin-bottom: 6px;`;
const NotaContenido = styled.div`font-size: ${p => p.theme.fontSizes.sm}; color: ${p => p.theme.colors.textPrimary}; white-space: pre-wrap;`;
const NotaDelBtn = styled.button`
  position: absolute; top: 8px; right: 8px; width: 24px; height: 24px;
  border: none; border-radius: 4px; background: ${p => p.theme.colors.danger}10;
  color: ${p => p.theme.colors.danger}; display: flex; align-items: center; justify-content: center;
  cursor: pointer; opacity: 0;
  ${NotaItem}:hover & { opacity: 1; }
  svg { font-size: 0.9rem; }
  &:hover { background: ${p => p.theme.colors.danger}25; }
`;

/* ─── Adjuntos ─── */
const UploadZone = styled.label`
  display: flex; align-items: center; justify-content: center; gap: 10px;
  padding: 24px; border: 2px dashed ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg}; cursor: pointer;
  color: ${p => p.theme.colors.textSecondary}; font-size: ${p => p.theme.fontSizes.sm};
  transition: all 0.15s; margin-bottom: ${p => p.theme.spacing.lg};
  svg { font-size: 1.5rem; }
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; background: ${p => p.theme.colors.primary}05; }
`;
const AdjGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${p => p.theme.spacing.md};
`;
const AdjItem = styled.div`
  background: ${p => p.theme.colors.background}; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; overflow: hidden;
  display: flex; flex-direction: column;
  &:hover .adj-actions { opacity: 1; }
`;
const AdjPreview = styled.div`
  height: 120px; display: flex; align-items: center; justify-content: center;
  background: ${p => p.theme.colors.backgroundLight}; overflow: hidden; position: relative;
  img { width: 100%; height: 100%; object-fit: cover; }
  svg { font-size: 3rem; color: ${p => p.theme.colors.textLight}; }
`;
const AdjActions = styled.div`
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; gap: 8px;
  opacity: 0; transition: opacity 0.15s;
  &.adj-actions { }
`;
const AdjActionBtn = styled.a`
  width: 36px; height: 36px; border-radius: 50%; background: white;
  display: flex; align-items: center; justify-content: center; cursor: pointer; text-decoration: none;
  color: ${p => p.del ? p.theme.colors.danger : p.theme.colors.primary};
  svg { font-size: 1.1rem; }
  &:hover { transform: scale(1.1); }
`;
const AdjActionDelBtn = styled.button`
  width: 36px; height: 36px; border-radius: 50%; background: white; border: none;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  color: ${p => p.theme.colors.danger};
  svg { font-size: 1.1rem; }
  &:hover { transform: scale(1.1); }
`;
const AdjInfo = styled.div`
  padding: 10px 12px;
`;
const AdjName = styled.div`
  font-size: ${p => p.theme.fontSizes.xs}; font-weight: 600; color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const AdjMeta = styled.div`font-size: 10px; color: ${p => p.theme.colors.textLight}; margin-top: 2px;`;

/* ─── Calendario ─── */
const CalSection = styled.div`display: flex; flex-direction: column; gap: ${p => p.theme.spacing.xl};`;
const CalCard = styled.div`
  background: ${p => p.theme.colors.background}; border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border}; padding: ${p => p.theme.spacing.lg};
`;
const CalSectionTitle = styled.h4`
  font-size: ${p => p.theme.fontSizes.md}; font-weight: 700; color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 14px 0; display: flex; align-items: center; gap: 8px;
  svg { color: ${p => p.theme.colors.primary}; }
`;
const Timeline = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const TimelineItem = styled.div`
  display: flex; align-items: flex-start; gap: 12px; padding: 10px 0;
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  &:last-child { border-bottom: none; }
`;
const TimelineDot = styled.div`
  width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; margin-top: 4px;
  background: ${p => p.color || '#ccc'};
`;
const TimelineContent = styled.div`flex: 1;`;
const TimelineTitle = styled.div`font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; color: ${p => p.theme.colors.textPrimary};`;
const TimelineDate = styled.div`font-size: ${p => p.theme.fontSizes.xs}; color: ${p => p.theme.colors.textSecondary}; margin-top: 2px;`;
const StatGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 12px;
`;
const StatCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight}; border-radius: ${p => p.theme.borderRadius.md};
  padding: 14px 16px; text-align: center; border: 1px solid ${p => p.theme.colors.border};
`;
const StatNum = styled.div`font-size: ${p => p.theme.fontSizes.xxl}; font-weight: 800; color: ${p => p.color || p.theme.colors.primary};`;
const StatLabel = styled.div`font-size: 10px; color: ${p => p.theme.colors.textSecondary}; font-weight: 600; text-transform: uppercase; margin-top: 2px;`;

const EmptyState = styled.div`
  text-align: center; padding: ${p => p.theme.spacing.xl} 0;
  color: ${p => p.theme.colors.textLight}; font-size: ${p => p.theme.fontSizes.sm};
`;

/* ─── Constants ─── */
const STATUS_CONFIG = [
  { key: 'pendiente',  label: 'Pendiente',  color: '#F6AD55', Icon: MdPendingActions },
  { key: 'en_proceso', label: 'En proceso', color: '#63B3ED', Icon: MdPlayArrow },
  { key: 'completado', label: 'Completado', color: '#68D391', Icon: MdCheckCircle },
  { key: 'cancelado',  label: 'Cancelado',  color: '#FC8181', Icon: MdCancel },
];
const PRIORIDAD_COLOR = { alta: '#FC8181', media: '#F6AD55', baja: '#68D391' };
const TAREA_ESTADO_CONFIG = {
  pendiente:   { label: 'Pendiente',   color: '#F6AD55', next: 'en_proceso' },
  en_proceso:  { label: 'En proceso',  color: '#63B3ED', next: 'completada' },
  completada:  { label: 'Completada',  color: '#68D391', next: 'pendiente' },
};
const formatPrice = p => Number(p || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
const fmtDate = d => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtSize = b => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(0)} KB` : `${(b/1048576).toFixed(1)} MB`;
const emptyItem = () => ({ id: Date.now(), inventario_item_id: '', nombre: '', cantidad: 1, precio_unitario: 0, total: 0, notas: '' });
const computeItemTotal = item => parseFloat(item.precio_unitario || 0) * parseFloat(item.cantidad || 1);

const AdjFileIcon = ({ mime }) => {
  if (mime?.startsWith('image/'))       return <MdImage />;
  if (mime === 'application/pdf')       return <MdPictureAsPdf />;
  if (mime?.includes('sheet') || mime?.includes('excel') || mime === 'text/csv') return <MdTableChart />;
  return <MdInsertDriveFile />;
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const PedidoDetail = () => {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const { confirm, toast } = useModal();
  const fileInputRef = useRef(null);

  /* ── Core state ── */
  const [pedido, setPedido]     = useState(null);
  const [clientes, setClientes] = useState([]);
  const [invItems, setInvItems] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ cliente_id: '', estado: 'pendiente', fecha_inicio: '', fecha_entrega: '', notas: '' });
  const [items, setItems] = useState([emptyItem()]);

  /* ── Recibo ── */
  const hoy  = new Date().toISOString().split('T')[0];
  const en30 = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
  const [showReciboForm, setShowReciboForm] = useState(false);
  const [reciboFechaVen, setReciboFechaVen] = useState(en30);
  const [generandoRecibo, setGenerandoRecibo] = useState(false);
  const [recibosPrevios, setRecibosPrevios] = useState([]);

  /* ── Tabs ── */
  const [activeTab, setActiveTab] = useState('general');

  /* ── Tareas ── */
  const [tareas, setTareas]           = useState([]);
  const [tareasLoaded, setTareasLoaded] = useState(false);
  const [showAddTarea, setShowAddTarea] = useState(false);
  const [savingTarea, setSavingTarea]   = useState(false);
  const [newTarea, setNewTarea] = useState({ titulo: '', descripcion: '', prioridad: 'media', fecha_vencimiento: '' });

  /* ── Gastos (solo proyecto) ── */
  const [gastos, setGastos]           = useState([]);
  const [gastosLoaded, setGastosLoaded] = useState(false);
  const [showAddGasto, setShowAddGasto] = useState(false);
  const [savingGasto, setSavingGasto]   = useState(false);
  const [newGasto, setNewGasto] = useState({ descripcion: '', monto: '', categoria: 'General', fecha: hoy, notas: '' });

  /* ── Notas ── */
  const [notas, setNotas]           = useState([]);
  const [notasLoaded, setNotasLoaded] = useState(false);
  const [newNotaText, setNewNotaText] = useState('');
  const [savingNota, setSavingNota]   = useState(false);

  /* ── Adjuntos ── */
  const [adjuntos, setAdjuntos]           = useState([]);
  const [adjuntosLoaded, setAdjuntosLoaded] = useState(false);
  const [uploadingAdj, setUploadingAdj]     = useState(false);

  /* ── Load core data ── */
  useEffect(() => {
    Promise.all([pedidosAPI.getById(id), clientesAPI.getAll(), inventarioAPI.getAll()])
      .then(([ped, cli, inv]) => {
        const d = ped.data.data;
        setPedido(d);
        setForm({
          cliente_id: d.cliente_id ? String(d.cliente_id) : '',
          estado: d.estado || 'pendiente',
          fecha_inicio: d.fecha_inicio ? d.fecha_inicio.split('T')[0] : '',
          fecha_entrega: d.fecha_entrega ? d.fecha_entrega.split('T')[0] : '',
          notas: d.notas || '',
        });
        setItems(d.items?.length
          ? d.items.map(i => ({ ...i, id: i.id, total: computeItemTotal(i) }))
          : [emptyItem()]);
        setClientes((cli.data.data || []).filter(c => c.estado === 'Activo'));
        setInvItems((inv.data.data || []).filter(i => i.activo));
        // Cargar tareas siempre (para el resumen sticky)
        proyectosAPI.getTareas(id)
          .then(r => { setTareas(r.data.data || []); setTareasLoaded(true); })
          .catch(() => {});
        // Gastos solo para proyectos
        if (d.tipo === 'proyecto') {
          proyectosAPI.getGastos(id)
            .then(r => { setGastos(r.data.data || []); setGastosLoaded(true); })
            .catch(() => {});
        }
      }).catch(() => toast('Error al cargar', 'error'))
        .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  /* ── Lazy loaders ── */
  const loadGastos = useCallback(async () => {
    if (gastosLoaded) return;
    try { const r = await proyectosAPI.getGastos(id); setGastos(r.data.data || []); setGastosLoaded(true); }
    catch {}
  }, [id, gastosLoaded]);

  const loadNotas = useCallback(async () => {
    if (notasLoaded) return;
    try { const r = await proyectosAPI.getNotas(id); setNotas(r.data.data || []); setNotasLoaded(true); }
    catch {}
  }, [id, notasLoaded]);

  const loadAdjuntos = useCallback(async () => {
    if (adjuntosLoaded) return;
    try { const r = await adjuntosAPI.getByEntidad('pedido', id); setAdjuntos(r.data.data || []); setAdjuntosLoaded(true); }
    catch {}
  }, [id, adjuntosLoaded]);

  useEffect(() => {
    if (activeTab === 'gastos'    || activeTab === 'calendario') loadGastos();
    if (activeTab === 'notas'     || activeTab === 'calendario') loadNotas();
    if (activeTab === 'adjuntos'  || activeTab === 'calendario') loadAdjuntos();
  }, [activeTab]); // eslint-disable-line

  const isProyecto = pedido?.tipo === 'proyecto';

  /* ── General handlers ── */
  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleItemChange = (idx, field, value) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'inventario_item_id') {
        if (value) {
          const inv = invItems.find(i => String(i.id) === String(value));
          if (inv) {
            next[idx].nombre = inv.nombre;
            next[idx].precio_unitario = Number(inv.precio) || 0;
            next[idx].stock_actual = inv.tipo === 'producto' ? Number(inv.stock ?? 0) : null;
          }
        } else { next[idx].stock_actual = null; }
      }
      next[idx].total = computeItemTotal(next[idx]);
      return next;
    });
  };

  const total = items.reduce((s, i) => s + (i.total || 0), 0);

  const handleSave = async () => {
    if (!isProyecto && form.estado === 'completado' && !pedido.stock_descontado) {
      const sinStock = items.filter(i => i.stock_actual != null && Number(i.cantidad) > i.stock_actual);
      if (sinStock.length > 0) {
        const ok = await confirm(
          `Algunos productos no tienen stock suficiente: ${sinStock.map(i => `${i.nombre} (${i.stock_actual} disponibles)`).join(', ')}. ¿Deseas continuar?`,
          { title: 'Stock insuficiente', confirmText: 'Completar de todas formas' }
        );
        if (!ok) return;
      }
    }
    setSubmitting(true);
    try {
      const clienteSel = clientes.find(c => String(c.id) === form.cliente_id);
      const res = await pedidosAPI.update(id, {
        ...form,
        cliente_id: form.cliente_id || null,
        cliente_nombre: clienteSel?.nombre || null,
        fecha_inicio: form.fecha_inicio || null,
        fecha_entrega: form.fecha_entrega || null,
        items: items.map(i => ({
          inventario_item_id: i.inventario_item_id || null,
          nombre: i.nombre || '',
          cantidad: Number(i.cantidad) || 1,
          precio_unitario: Number(i.precio_unitario) || 0,
          notas: i.notas || null,
        })),
      });
      setPedido(res.data.data);
      toast('Guardado correctamente', 'success');
    } catch (err) { toast(err.message || 'Error al guardar', 'error'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    const ok = await confirm(`¿Eliminar ${pedido?.numero}?`, {
      title: `Eliminar ${isProyecto ? 'proyecto' : 'pedido'}`, confirmText: 'Eliminar', danger: true,
    });
    if (!ok) return;
    try { await pedidosAPI.delete(id); toast('Eliminado', 'success'); navigate('/pedidos'); }
    catch (err) { toast(err.message || 'Error', 'error'); }
  };

  const handleToggleReciboForm = async () => {
    if (!showReciboForm && pedido?.cliente_id) {
      try {
        const res = await recibosAPI.getAll({ clienteId: pedido.cliente_id });
        const desc = `${pedido.tipo === 'proyecto' ? 'Proyecto' : 'Pedido'} ${pedido.numero}`;
        setRecibosPrevios((res.data?.data || []).filter(r => r.descripcion === desc));
      } catch { setRecibosPrevios([]); }
    }
    setShowReciboForm(o => !o);
  };

  const handleGenerarRecibo = async () => {
    if (!pedido.cliente_id) { toast('El pedido necesita un cliente registrado para generar un recibo', 'error'); return; }
    if (recibosPrevios.length > 0) {
      const label = recibosPrevios.length === 1 ? '1 recibo' : `${recibosPrevios.length} recibos`;
      const tipo  = pedido.tipo === 'proyecto' ? 'proyecto' : 'pedido';
      const ok = await confirm(`Ya existen ${label} generados desde este ${tipo}. ¿Crear uno adicional de todas formas?`, {
        title: 'Recibo existente',
        confirmText: 'Crear de todas formas',
        cancelText: 'Cancelar',
      });
      if (!ok) return;
    }
    setGenerandoRecibo(true);
    try {
      const res = await recibosAPI.create({
        cliente_id: pedido.cliente_id,
        numero_recibo: `REC-${Date.now()}`,
        descripcion: `${isProyecto ? 'Proyecto' : 'Pedido'} ${pedido.numero}`,
        fecha_emision: hoy, fecha_vencimiento: reciboFechaVen, notas: pedido.notas || null,
        items: items.map(i => ({ descripcion: i.nombre || 'Item', cantidad: Number(i.cantidad) || 1, precio_unitario: Number(i.precio_unitario) || 0 })),
      });
      toast('Recibo generado correctamente', 'success');
      navigate(`/recibos/${res.data.data.id}`);
    } catch (err) { toast(err.message || 'Error al generar recibo', 'error'); }
    finally { setGenerandoRecibo(false); }
  };

  /* ── Tareas handlers ── */
  const handleSaveTarea = async () => {
    if (!newTarea.titulo.trim()) { toast('El título es obligatorio', 'error'); return; }
    setSavingTarea(true);
    try {
      const r = await proyectosAPI.createTarea(id, newTarea);
      setTareas(prev => [...prev, r.data.data]);
      setNewTarea({ titulo: '', descripcion: '', prioridad: 'media', fecha_vencimiento: '' });
      setShowAddTarea(false);
      toast('Tarea agregada', 'success');
    } catch (err) { toast(err.message || 'Error', 'error'); }
    finally { setSavingTarea(false); }
  };

  const handleToggleTarea = async (tarea) => {
    const next = TAREA_ESTADO_CONFIG[tarea.estado]?.next || 'pendiente';
    try {
      const r = await proyectosAPI.updateTarea(id, tarea.id, { ...tarea, estado: next });
      setTareas(prev => prev.map(t => t.id === tarea.id ? r.data.data : t));
    } catch (err) { toast(err.message || 'Error', 'error'); }
  };

  const handleDeleteTarea = async (tid) => {
    const ok = await confirm('¿Eliminar esta tarea?', { title: 'Eliminar tarea', confirmText: 'Eliminar', danger: true });
    if (!ok) return;
    try {
      await proyectosAPI.deleteTarea(id, tid);
      setTareas(prev => prev.filter(t => t.id !== tid));
      toast('Tarea eliminada', 'success');
    } catch (err) { toast(err.message || 'Error', 'error'); }
  };

  /* ── Gastos handlers ── */
  const handleSaveGasto = async () => {
    if (!newGasto.descripcion.trim() || !newGasto.monto) { toast('Descripción y monto son obligatorios', 'error'); return; }
    setSavingGasto(true);
    try {
      const r = await proyectosAPI.createGasto(id, newGasto);
      setGastos(prev => [...prev, r.data.data]);
      setNewGasto({ descripcion: '', monto: '', categoria: 'General', fecha: hoy, notas: '' });
      setShowAddGasto(false);
      toast('Gasto registrado', 'success');
    } catch (err) { toast(err.message || 'Error', 'error'); }
    finally { setSavingGasto(false); }
  };

  const handleDeleteGasto = async (gid) => {
    const ok = await confirm('¿Eliminar este gasto?', { title: 'Eliminar gasto', confirmText: 'Eliminar', danger: true });
    if (!ok) return;
    try {
      await proyectosAPI.deleteGasto(id, gid);
      setGastos(prev => prev.filter(g => g.id !== gid));
      toast('Gasto eliminado', 'success');
    } catch (err) { toast(err.message || 'Error', 'error'); }
  };

  /* ── Notas handlers ── */
  const handleSaveNota = async () => {
    if (!newNotaText.trim()) return;
    setSavingNota(true);
    try {
      const r = await proyectosAPI.createNota(id, { contenido: newNotaText });
      setNotas(prev => [r.data.data, ...prev]);
      setNewNotaText('');
      toast('Nota agregada', 'success');
    } catch (err) { toast(err.message || 'Error', 'error'); }
    finally { setSavingNota(false); }
  };

  const handleDeleteNota = async (nid) => {
    try {
      await proyectosAPI.deleteNota(id, nid);
      setNotas(prev => prev.filter(n => n.id !== nid));
    } catch (err) { toast(err.message || 'Error', 'error'); }
  };

  /* ── Adjuntos handlers ── */
  const handleUploadAdj = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingAdj(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const r = await adjuntosAPI.upload('pedido', id, fd);
      setAdjuntos(prev => [r.data.data, ...prev]);
      setAdjuntosLoaded(true);
      toast('Archivo subido', 'success');
    } catch (err) { toast(err.message || 'Error al subir archivo', 'error'); }
    finally { setUploadingAdj(false); }
  };

  const handleDeleteAdj = async (adjId) => {
    const ok = await confirm('¿Eliminar este archivo?', { title: 'Eliminar adjunto', confirmText: 'Eliminar', danger: true });
    if (!ok) return;
    try {
      await adjuntosAPI.delete(adjId);
      setAdjuntos(prev => prev.filter(a => a.id !== adjId));
    } catch (err) { toast(err.message || 'Error', 'error'); }
  };

  /* ── Render ── */
  if (loading) return <Container><p>Cargando...</p></Container>;
  if (!pedido)  return <Container><p>No encontrado.</p></Container>;

  const totalGastos       = gastos.reduce((s, g) => s + Number(g.monto || 0), 0);
  const tareasCompletadas = tareas.filter(t => t.estado === 'completada').length;
  const tareasPct         = tareas.length ? Math.round((tareasCompletadas / tareas.length) * 100) : 0;
  const tareasVencidas    = tareas.filter(t => t.fecha_vencimiento && new Date(t.fecha_vencimiento) < new Date() && t.estado !== 'completada').length;

  return (
    <Container>
      {/* ── Header ── */}
      <PageHeader>
        <HeaderLeft>
          <BackButton onClick={() => navigate(-1)}><MdArrowBack /></BackButton>
          <Title>
            {isProyecto ? <MdWorkspaces /> : <MdShoppingCart />}
            {pedido.numero}
          </Title>
        </HeaderLeft>
        <HeaderActions>
          <GenReciboBtn onClick={handleToggleReciboForm}>
            <MdReceipt /> Generar Recibo
          </GenReciboBtn>
          <DeleteBtn onClick={handleDelete}><MdDelete /> Eliminar</DeleteBtn>
        </HeaderActions>
      </PageHeader>

      {/* ── Recibo form ── */}
      {showReciboForm && (
        <ReciboForm>
          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
            Generar recibo desde este {isProyecto ? 'proyecto' : 'pedido'}
          </div>
          {recibosPrevios.length > 0 && (
            <StockBanner>
              <MdWarning />
              {`Ya ${recibosPrevios.length === 1 ? 'existe 1 recibo generado' : `existen ${recibosPrevios.length} recibos generados`} desde este ${isProyecto ? 'proyecto' : 'pedido'}:`}
              {recibosPrevios.map(r => (
                <span
                  key={r.id}
                  onClick={() => navigate(`/recibos/${r.id}`)}
                  style={{ cursor: 'pointer', textDecoration: 'underline', marginLeft: 8, fontWeight: 600 }}
                >
                  {r.numero_recibo}
                </span>
              ))}
            </StockBanner>
          )}
          {!pedido.cliente_id && (
            <StockBanner><MdWarning /> Asigna un cliente antes de generar el recibo.</StockBanner>
          )}
          <ReciboFormRow>
            <div>
              <ReciboLabel>Fecha de vencimiento</ReciboLabel>
              <ReciboInput type="date" value={reciboFechaVen} onChange={e => setReciboFechaVen(e.target.value)} min={hoy} />
            </div>
            <ReciboBtn type="button" onClick={handleGenerarRecibo} disabled={generandoRecibo || !pedido.cliente_id}>
              <MdReceipt /> {generandoRecibo ? 'Generando...' : 'Crear recibo'}
            </ReciboBtn>
          </ReciboFormRow>
        </ReciboForm>
      )}

      {/* ── Status strip ── */}
      <StatusStrip>
        {STATUS_CONFIG.map(s => (
          <StatusStep key={s.key} active={form.estado === s.key} color={s.color} type="button"
            onClick={() => setForm(prev => ({ ...prev, estado: s.key }))}>
            <s.Icon /> {s.label}
          </StatusStep>
        ))}
      </StatusStrip>

      {/* ── Resumen sticky ── */}
      <StickyResumen>
        <ResumenRow>
          <ResumenStat>
            <ResumenNum>{tareas.length}</ResumenNum>
            <ResumenLabel>Tareas</ResumenLabel>
          </ResumenStat>
          <ResumenStat>
            <ResumenNum color="#68D391">{tareasCompletadas}</ResumenNum>
            <ResumenLabel>Listas</ResumenLabel>
          </ResumenStat>
          <ResumenStat>
            <ResumenNum color="#F6AD55">{tareas.filter(t => t.estado === 'en_proceso').length}</ResumenNum>
            <ResumenLabel>En proceso</ResumenLabel>
          </ResumenStat>
          {tareasVencidas > 0 && (
            <ResumenStat>
              <ResumenNum color="#FC8181">{tareasVencidas}</ResumenNum>
              <ResumenLabel>Vencidas</ResumenLabel>
            </ResumenStat>
          )}
          {isProyecto && (
            <>
              <ResumenDivider />
              <ResumenStat>
                <ResumenNum>{formatPrice(totalGastos)}</ResumenNum>
                <ResumenLabel>Gastos</ResumenLabel>
              </ResumenStat>
            </>
          )}
          <ResumenDivider />
          <ResumenStat>
            <ResumenNum>{adjuntos.length}</ResumenNum>
            <ResumenLabel>Adjuntos</ResumenLabel>
          </ResumenStat>
          <ResumenDivider />
          <ResumenProgress>
            <ResumenProgressLabel>
              <span>Progreso de tareas</span>
              <span>{tareasPct}%</span>
            </ResumenProgressLabel>
            <ProgressBar><ProgressFill pct={tareasPct} /></ProgressBar>
          </ResumenProgress>
        </ResumenRow>
      </StickyResumen>

      {/* ── Tabs ── */}
      <TabBar>
        <Tab active={activeTab === 'general'} onClick={() => setActiveTab('general')}>
          {isProyecto ? <MdWorkspaces /> : <MdShoppingCart />} General
        </Tab>
        <Tab active={activeTab === 'tareas'} onClick={() => setActiveTab('tareas')}>
          <MdChecklist /> Tareas {tareas.length > 0 && `(${tareasCompletadas}/${tareas.length})`}
        </Tab>
        {isProyecto && (
          <Tab active={activeTab === 'gastos'} onClick={() => setActiveTab('gastos')}>
            <MdAttachMoney /> Gastos {gastos.length > 0 && `(${formatPrice(totalGastos)})`}
          </Tab>
        )}
        <Tab active={activeTab === 'notas'} onClick={() => setActiveTab('notas')}>
          <MdStickyNote2 /> Notas {notas.length > 0 && `(${notas.length})`}
        </Tab>
        <Tab active={activeTab === 'adjuntos'} onClick={() => setActiveTab('adjuntos')}>
          <MdAttachFile /> Adjuntos {adjuntos.length > 0 && `(${adjuntos.length})`}
        </Tab>
        <Tab active={activeTab === 'calendario'} onClick={() => setActiveTab('calendario')}>
          <MdCalendarMonth /> Calendario
        </Tab>
      </TabBar>

      {/* ══════════════ TAB: GENERAL ══════════════ */}
      {activeTab === 'general' && (
        <>
          <FormCard>
            <CardTitle>Datos del {isProyecto ? 'Proyecto' : 'Pedido'}</CardTitle>
            <Grid>
              <FieldGroup>
                <Label>Cliente</Label>
                <Select name="cliente_id" value={form.cliente_id} onChange={handleChange}>
                  <option value="">Sin cliente</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </Select>
              </FieldGroup>
              <FieldGroup>
                <Label>{isProyecto ? 'Fecha de inicio' : 'Fecha del pedido'}</Label>
                <Input type="date" name="fecha_inicio" value={form.fecha_inicio} onChange={handleChange} />
              </FieldGroup>
              <FieldGroup>
                <Label>Fecha de entrega</Label>
                <Input type="date" name="fecha_entrega" value={form.fecha_entrega} onChange={handleChange} />
              </FieldGroup>
              <FieldGroup style={{ gridColumn: '1 / -1' }}>
                <Label>Notas</Label>
                <TextArea name="notas" value={form.notas} onChange={handleChange} />
              </FieldGroup>
            </Grid>
          </FormCard>

          <FormCard>
            <CardTitle>{isProyecto ? 'Servicios' : 'Productos'}</CardTitle>
            {!isProyecto && pedido.stock_descontado && (
              <StockBanner success><MdDone /> Stock descontado del inventario</StockBanner>
            )}
            {items.map((item, idx) => (
              <ItemRow key={item.id}>
                <ItemFieldGroup>
                  <ItemLabel>{isProyecto ? 'Servicio' : 'Producto'} <span style={{ color: 'red' }}>*</span></ItemLabel>
                  {invItems.length > 0 && (
                    <ItemInput as="select" value={item.inventario_item_id || ''}
                      onChange={e => handleItemChange(idx, 'inventario_item_id', e.target.value)}
                      style={{ marginBottom: 6 }}>
                      <option value="">— Del catálogo —</option>
                      {invItems.map(i => <option key={i.id} value={i.id}>{i.nombre}</option>)}
                    </ItemInput>
                  )}
                  <ItemInput value={item.nombre} onChange={e => handleItemChange(idx, 'nombre', e.target.value)} placeholder="Nombre del item" />
                  {!isProyecto && item.stock_actual != null && (
                    <StockTag warn={Number(item.cantidad) > item.stock_actual}>
                      {Number(item.cantidad) > item.stock_actual
                        ? <><MdWarning /> Sin stock suficiente ({item.stock_actual} disponibles)</>
                        : `Stock: ${item.stock_actual}`}
                    </StockTag>
                  )}
                </ItemFieldGroup>
                <ItemFieldGroup>
                  <ItemLabel>Cantidad</ItemLabel>
                  <ItemInput type="number" min="1" value={item.cantidad} onChange={e => handleItemChange(idx, 'cantidad', e.target.value)} />
                </ItemFieldGroup>
                <ItemFieldGroup>
                  <ItemLabel>Precio unit. ($)</ItemLabel>
                  <ItemInput type="number" min="0" step="0.01" value={item.precio_unitario} onChange={e => handleItemChange(idx, 'precio_unitario', e.target.value)} />
                </ItemFieldGroup>
                <ItemFieldGroup>
                  <ItemLabel>Total ($)</ItemLabel>
                  <ItemInput type="number" value={(item.total || 0).toFixed(2)} readOnly />
                </ItemFieldGroup>
                <DelBtn type="button" onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))}
                  disabled={items.length === 1} style={{ opacity: items.length === 1 ? 0.4 : 1, alignSelf: 'flex-end' }}>
                  <MdDelete />
                </DelBtn>
              </ItemRow>
            ))}
            <AddItemBtn type="button" onClick={() => setItems(prev => [...prev, emptyItem()])}>
              <MdAdd /> Agregar item
            </AddItemBtn>
            <TotalRow>Total: {formatPrice(total)}</TotalRow>
          </FormCard>

          <ButtonRow>
            <Btn type="button" onClick={() => navigate(-1)}>Cancelar</Btn>
            <Btn type="button" primary onClick={handleSave} disabled={submitting}>
              <MdSave /> {submitting ? 'Guardando...' : 'Guardar cambios'}
            </Btn>
          </ButtonRow>
        </>
      )}

      {/* ══════════════ TAB: TAREAS ══════════════ */}
      {activeTab === 'tareas' && (
        <FormCard>
          <CardTitle><MdChecklist /> Tareas</CardTitle>

          {tareas.length === 0 ? (
            <EmptyState>Sin tareas aún. Agrega la primera abajo.</EmptyState>
          ) : (
            <TareaList>
              {tareas.map(t => {
                const est = TAREA_ESTADO_CONFIG[t.estado] || TAREA_ESTADO_CONFIG.pendiente;
                return (
                  <TareaItem key={t.id} color={PRIORIDAD_COLOR[t.prioridad]} done={t.estado === 'completada'}>
                    <TareaCheck done={t.estado === 'completada'} onClick={() => handleToggleTarea(t)}>
                      {t.estado === 'completada' && <MdDone />}
                    </TareaCheck>
                    <TareaInfo>
                      <TareaTitulo done={t.estado === 'completada'}>{t.titulo}</TareaTitulo>
                      {t.descripcion && <TareaDesc>{t.descripcion}</TareaDesc>}
                      <TareaMeta>
                        <Badge bg={est.color}>{est.label}</Badge>
                        <Badge bg={PRIORIDAD_COLOR[t.prioridad]}><MdFlag style={{ fontSize: 10 }} /> {t.prioridad}</Badge>
                        {t.fecha_vencimiento && <Badge bg="#63B3ED"><MdCalendarMonth style={{ fontSize: 10 }} /> {fmtDate(t.fecha_vencimiento)}</Badge>}
                      </TareaMeta>
                    </TareaInfo>
                    <DelBtn type="button" onClick={() => handleDeleteTarea(t.id)}><MdDelete /></DelBtn>
                  </TareaItem>
                );
              })}
            </TareaList>
          )}

          <ToggleAddBtn onClick={() => setShowAddTarea(o => !o)}>
            {showAddTarea ? <MdKeyboardArrowUp /> : <MdAdd />}
            {showAddTarea ? 'Cancelar' : 'Nueva tarea'}
          </ToggleAddBtn>

          {showAddTarea && (
            <AddForm>
              <SmInput
                placeholder="Título de la tarea *"
                value={newTarea.titulo}
                onChange={e => setNewTarea(p => ({ ...p, titulo: e.target.value }))}
              />
              <SmTextArea
                placeholder="Descripción (opcional)"
                value={newTarea.descripcion}
                onChange={e => setNewTarea(p => ({ ...p, descripcion: e.target.value }))}
              />
              <AddFormRow>
                <div>
                  <ItemLabel>Prioridad</ItemLabel>
                  <SmSelect value={newTarea.prioridad} onChange={e => setNewTarea(p => ({ ...p, prioridad: e.target.value }))}>
                    <option value="baja">Baja</option>
                    <option value="media">Media</option>
                    <option value="alta">Alta</option>
                  </SmSelect>
                </div>
                <div>
                  <ItemLabel>Fecha límite</ItemLabel>
                  <SmInput type="date" value={newTarea.fecha_vencimiento} onChange={e => setNewTarea(p => ({ ...p, fecha_vencimiento: e.target.value }))} />
                </div>
              </AddFormRow>
              <SaveBtn onClick={handleSaveTarea} disabled={savingTarea}>
                <MdAdd /> {savingTarea ? 'Guardando...' : 'Agregar tarea'}
              </SaveBtn>
            </AddForm>
          )}
        </FormCard>
      )}

      {/* ══════════════ TAB: GASTOS (solo proyecto) ══════════════ */}
      {isProyecto && activeTab === 'gastos' && (
        <FormCard>
          <CardTitle><MdAttachMoney /> Registro de gastos</CardTitle>

          {gastos.length === 0 ? (
            <EmptyState>Sin gastos registrados aún.</EmptyState>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <GastoTable>
                <thead>
                  <tr>
                    <GTh>Descripción</GTh>
                    <GTh>Categoría</GTh>
                    <GTh>Fecha</GTh>
                    <GTh style={{ textAlign: 'right' }}>Monto</GTh>
                    <GTh></GTh>
                  </tr>
                </thead>
                <tbody>
                  {gastos.map(g => (
                    <tr key={g.id}>
                      <GTd>
                        <div style={{ fontWeight: 600 }}>{g.descripcion}</div>
                        {g.notas && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{g.notas}</div>}
                      </GTd>
                      <GTd><CatBadge>{g.categoria}</CatBadge></GTd>
                      <GTd>{fmtDate(g.fecha)}</GTd>
                      <GTd style={{ textAlign: 'right', fontWeight: 700 }}>{formatPrice(g.monto)}</GTd>
                      <GTd>
                        <DelBtn type="button" onClick={() => handleDeleteGasto(g.id)}><MdDelete /></DelBtn>
                      </GTd>
                    </tr>
                  ))}
                </tbody>
              </GastoTable>
              <GastoTotal>
                <span>Total gastos:</span>
                <span>{formatPrice(totalGastos)}</span>
              </GastoTotal>
            </div>
          )}

          <ToggleAddBtn onClick={() => setShowAddGasto(o => !o)}>
            {showAddGasto ? <MdKeyboardArrowUp /> : <MdAdd />}
            {showAddGasto ? 'Cancelar' : 'Registrar gasto'}
          </ToggleAddBtn>

          {showAddGasto && (
            <AddForm>
              <AddFormRow>
                <div style={{ gridColumn: '1 / -1' }}>
                  <ItemLabel>Descripción *</ItemLabel>
                  <SmInput placeholder="Ej: Material de construcción" value={newGasto.descripcion}
                    onChange={e => setNewGasto(p => ({ ...p, descripcion: e.target.value }))} />
                </div>
                <div>
                  <ItemLabel>Monto ($) *</ItemLabel>
                  <SmInput type="number" min="0" step="0.01" placeholder="0.00" value={newGasto.monto}
                    onChange={e => setNewGasto(p => ({ ...p, monto: e.target.value }))} />
                </div>
                <div>
                  <ItemLabel>Categoría</ItemLabel>
                  <SmSelect value={newGasto.categoria} onChange={e => setNewGasto(p => ({ ...p, categoria: e.target.value }))}>
                    {['General','Materiales','Mano de obra','Transporte','Software','Equipos','Otros'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </SmSelect>
                </div>
                <div>
                  <ItemLabel>Fecha</ItemLabel>
                  <SmInput type="date" value={newGasto.fecha} onChange={e => setNewGasto(p => ({ ...p, fecha: e.target.value }))} />
                </div>
              </AddFormRow>
              <SmTextArea placeholder="Notas adicionales (opcional)" value={newGasto.notas}
                onChange={e => setNewGasto(p => ({ ...p, notas: e.target.value }))} />
              <SaveBtn onClick={handleSaveGasto} disabled={savingGasto}>
                <MdAdd /> {savingGasto ? 'Guardando...' : 'Registrar gasto'}
              </SaveBtn>
            </AddForm>
          )}
        </FormCard>
      )}

      {/* ══════════════ TAB: NOTAS ══════════════ */}
      {activeTab === 'notas' && (
        <FormCard>
          <CardTitle><MdStickyNote2 /> Notas y comentarios</CardTitle>

          <AddForm style={{ marginTop: 0 }}>
            <SmTextArea
              placeholder="Escribe una nota o comentario..."
              value={newNotaText}
              onChange={e => setNewNotaText(e.target.value)}
              style={{ minHeight: 80 }}
            />
            <SaveBtn onClick={handleSaveNota} disabled={savingNota || !newNotaText.trim()}>
              <MdNote /> {savingNota ? 'Guardando...' : 'Agregar nota'}
            </SaveBtn>
          </AddForm>

          {notas.length === 0 ? (
            <EmptyState style={{ marginTop: 16 }}>Sin notas aún.</EmptyState>
          ) : (
            <NotaList style={{ marginTop: 16 }}>
              {notas.map(n => (
                <NotaItem key={n.id}>
                  <NotaFecha>{new Date(n.created_at).toLocaleString('es-MX')}</NotaFecha>
                  <NotaContenido>{n.contenido}</NotaContenido>
                  <NotaDelBtn onClick={() => handleDeleteNota(n.id)}><MdDelete /></NotaDelBtn>
                </NotaItem>
              ))}
            </NotaList>
          )}
        </FormCard>
      )}

      {/* ══════════════ TAB: ADJUNTOS ══════════════ */}
      {activeTab === 'adjuntos' && (
        <FormCard>
          <CardTitle><MdAttachFile /> Archivos adjuntos</CardTitle>

          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            onChange={handleUploadAdj}
          />
          <UploadZone htmlFor="adj-upload" onClick={() => fileInputRef.current?.click()}>
            <MdAttachFile />
            {uploadingAdj ? 'Subiendo...' : 'Haz clic para subir un archivo (imagen, PDF, Word, Excel — máx. 20MB)'}
          </UploadZone>

          {adjuntos.length === 0 ? (
            <EmptyState>Sin archivos adjuntos.</EmptyState>
          ) : (
            <AdjGrid>
              {adjuntos.map(a => {
                const url = adjuntosAPI.getUrl(a.nombre_archivo);
                const isImg = a.tipo_mime?.startsWith('image/');
                return (
                  <AdjItem key={a.id}>
                    <AdjPreview>
                      {isImg
                        ? <img src={url} alt={a.nombre_original} />
                        : <AdjFileIcon mime={a.tipo_mime} />
                      }
                      <AdjActions className="adj-actions">
                        <AdjActionBtn href={url} target="_blank" rel="noopener noreferrer" title="Abrir">
                          <MdOpenInNew />
                        </AdjActionBtn>
                        <AdjActionDelBtn onClick={() => handleDeleteAdj(a.id)} title="Eliminar">
                          <MdDelete />
                        </AdjActionDelBtn>
                      </AdjActions>
                    </AdjPreview>
                    <AdjInfo>
                      <AdjName title={a.nombre_original}>{a.nombre_original}</AdjName>
                      <AdjMeta>{fmtSize(a.tamanio)} · {fmtDate(a.created_at)}</AdjMeta>
                    </AdjInfo>
                  </AdjItem>
                );
              })}
            </AdjGrid>
          )}
        </FormCard>
      )}

      {/* ══════════════ TAB: CALENDARIO ══════════════ */}
      {activeTab === 'calendario' && (
        <CalSection>
          <FormCard>
            <CardTitle><MdCalendarMonth /> Resumen</CardTitle>
            <StatGrid>
              <StatCard><StatNum>{tareas.length}</StatNum><StatLabel>Tareas</StatLabel></StatCard>
              <StatCard><StatNum color="#68D391">{tareasCompletadas}</StatNum><StatLabel>Completadas</StatLabel></StatCard>
              <StatCard><StatNum color="#F6AD55">{tareas.filter(t => t.estado === 'en_proceso').length}</StatNum><StatLabel>En proceso</StatLabel></StatCard>
              <StatCard><StatNum color="#FC8181">{tareasVencidas}</StatNum><StatLabel>Vencidas</StatLabel></StatCard>
              {isProyecto && <StatCard><StatNum>{formatPrice(totalGastos)}</StatNum><StatLabel>Gastos</StatLabel></StatCard>}
              <StatCard><StatNum>{adjuntos.length}</StatNum><StatLabel>Adjuntos</StatLabel></StatCard>
              <StatCard><StatNum>{notas.length}</StatNum><StatLabel>Notas</StatLabel></StatCard>
            </StatGrid>
            {tareas.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                  <span>Progreso</span><span>{tareasPct}%</span>
                </div>
                <ProgressBar><ProgressFill pct={tareasPct} /></ProgressBar>
              </div>
            )}
          </FormCard>

          <FormCard>
            <CardTitle><MdCalendarMonth /> Línea de tiempo</CardTitle>
            <CalCard>
              <CalSectionTitle><MdCircle style={{ color: '#63B3ED', fontSize: 12 }} /> Fechas</CalSectionTitle>
              <Timeline>
                {form.fecha_inicio && (
                  <TimelineItem>
                    <TimelineDot color="#68D391" />
                    <TimelineContent>
                      <TimelineTitle>Inicio</TimelineTitle>
                      <TimelineDate>{fmtDate(form.fecha_inicio)}</TimelineDate>
                    </TimelineContent>
                  </TimelineItem>
                )}
                {[...tareas]
                  .filter(t => t.fecha_vencimiento)
                  .sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento))
                  .map(t => {
                    const vencida = new Date(t.fecha_vencimiento) < new Date() && t.estado !== 'completada';
                    const color = t.estado === 'completada' ? '#68D391' : vencida ? '#FC8181' : PRIORIDAD_COLOR[t.prioridad];
                    return (
                      <TimelineItem key={t.id}>
                        <TimelineDot color={color} />
                        <TimelineContent>
                          <TimelineTitle style={{ textDecoration: t.estado === 'completada' ? 'line-through' : 'none' }}>
                            {t.titulo}
                          </TimelineTitle>
                          <TimelineDate>
                            {fmtDate(t.fecha_vencimiento)} · <Badge bg={TAREA_ESTADO_CONFIG[t.estado]?.color}>{TAREA_ESTADO_CONFIG[t.estado]?.label}</Badge>
                            {vencida && <Badge bg="#FC8181" style={{ marginLeft: 4 }}>Vencida</Badge>}
                          </TimelineDate>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                {form.fecha_entrega && (
                  <TimelineItem>
                    <TimelineDot color="#FC8181" />
                    <TimelineContent>
                      <TimelineTitle>Entrega</TimelineTitle>
                      <TimelineDate>{fmtDate(form.fecha_entrega)}</TimelineDate>
                    </TimelineContent>
                  </TimelineItem>
                )}
                {!form.fecha_inicio && !form.fecha_entrega && tareas.filter(t => t.fecha_vencimiento).length === 0 && (
                  <EmptyState>Sin fechas configuradas.</EmptyState>
                )}
              </Timeline>
            </CalCard>
          </FormCard>

          {isProyecto && gastos.length > 0 && (
            <FormCard>
              <CardTitle><MdAttachMoney /> Gastos por categoría</CardTitle>
              {Object.entries(
                gastos.reduce((acc, g) => { acc[g.categoria] = (acc[g.categoria] || 0) + Number(g.monto); return acc; }, {})
              ).sort((a, b) => b[1] - a[1]).map(([cat, monto]) => {
                const pct = totalGastos ? Math.round((monto / totalGastos) * 100) : 0;
                return (
                  <div key={cat} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>{cat}</span>
                      <span>{formatPrice(monto)} ({pct}%)</span>
                    </div>
                    <ProgressBar><ProgressFill pct={pct} /></ProgressBar>
                  </div>
                );
              })}
            </FormCard>
          )}
        </CalSection>
      )}
    </Container>
  );
};

export default PedidoDetail;
